"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { SEASONS } from "@/lib/seasons/config";
import {
  getRollingWindow,
  seasonsInWindow,
  layoutRows,
  bandColourFor,
  CATEGORY_COLOURS,
  type RollingWindow,
} from "@/lib/seasons/windows";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatRange(startIso: string, endIso: string): string {
  const s = new Date(`${startIso}T00:00:00Z`);
  const e = new Date(`${endIso}T00:00:00Z`);
  const sMonth = MONTH_LABELS[s.getUTCMonth()];
  const eMonth = MONTH_LABELS[e.getUTCMonth()];
  const sYear = s.getUTCFullYear();
  const eYear = e.getUTCFullYear();
  if (sYear === eYear) return `${sMonth} – ${eMonth} ${eYear}`;
  return `${sMonth} ${sYear} – ${eMonth} ${eYear}`;
}

function monthTicks(w: RollingWindow): { label: string; leftPct: number }[] {
  const ticks: { label: string; leftPct: number }[] = [];
  const span = w.end.getTime() - w.start.getTime();
  const first = new Date(
    Date.UTC(w.start.getUTCFullYear(), w.start.getUTCMonth() + 1, 1),
  );
  const cursor = new Date(first);
  while (cursor < w.end) {
    const leftPct = ((cursor.getTime() - w.start.getTime()) / span) * 100;
    ticks.push({ label: MONTH_LABELS[cursor.getUTCMonth()], leftPct });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return ticks;
}

function verticalMonthTicks(w: RollingWindow): { label: string; topPct: number }[] {
  const ticks: { label: string; topPct: number }[] = [];
  const span = w.end.getTime() - w.start.getTime();
  const first = new Date(
    Date.UTC(w.start.getUTCFullYear(), w.start.getUTCMonth() + 1, 1),
  );
  const cursor = new Date(first);
  while (cursor < w.end) {
    const topPct = ((cursor.getTime() - w.start.getTime()) / span) * 100;
    ticks.push({ label: MONTH_LABELS[cursor.getUTCMonth()], topPct });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return ticks;
}

export function CalendarClient() {
  const today = useMemo(() => new Date(), []);
  const window = useMemo(() => getRollingWindow(today), [today]);
  const positioned = useMemo(
    () => seasonsInWindow(SEASONS, window),
    [window],
  );
  const rows = useMemo(() => layoutRows(positioned), [positioned]);
  const rowCount = rows.length ? Math.max(...rows) + 1 : 1;
  const ticks = useMemo(() => monthTicks(window), [window]);
  const vTicks = useMemo(() => verticalMonthTicks(window), [window]);

  const [activeSlug, setActiveSlug] = useState<string | null>(null);

  const BAND_HEIGHT = 40;
  const ROW_GAP = 10;
  const timelineHeight = rowCount * (BAND_HEIGHT + ROW_GAP) + 8;

  const activeSeason = activeSlug
    ? positioned.find((p) => p.slug === activeSlug) ?? null
    : null;

  return (
    <div>
      {/* Desktop horizontal timeline */}
      <div className="hidden md:block">
        <div className="relative border border-border-light rounded-[var(--radius-md)] bg-white p-6">
          {/* Month header */}
          <div className="relative h-6 mb-3">
            {ticks.map((t, i) => (
              <div
                key={i}
                className="absolute top-0 -translate-x-1/2 text-[11px] font-semibold uppercase tracking-[1.5px] text-text-muted"
                style={{ left: `${t.leftPct}%` }}
              >
                {t.label}
              </div>
            ))}
          </div>

          {/* Timeline body */}
          <div className="relative" style={{ height: `${timelineHeight}px` }}>
            {/* Month gridlines */}
            {ticks.map((t, i) => (
              <div
                key={i}
                className="absolute top-0 bottom-0 w-px bg-border-light"
                style={{ left: `${t.leftPct}%` }}
                aria-hidden
              />
            ))}

            {/* Today marker */}
            <div
              className="absolute top-0 bottom-0 w-px bg-gold"
              style={{ left: "0%" }}
              aria-hidden
            >
              <span className="absolute -top-[22px] text-[10px] font-semibold uppercase tracking-[1.5px] text-gold whitespace-nowrap">
                Today
              </span>
            </div>

            {/* Bands */}
            {positioned.map((p, i) => {
              const row = rows[i];
              const top = row * (BAND_HEIGHT + ROW_GAP);
              const colour = bandColourFor(p.category, p.slug);
              const isActive = activeSlug === p.slug;
              return (
                <Link
                  key={p.slug}
                  href={p.detailUrl}
                  onMouseEnter={() => setActiveSlug(p.slug)}
                  onMouseLeave={() => setActiveSlug(null)}
                  onFocus={() => setActiveSlug(p.slug)}
                  onBlur={() => setActiveSlug(null)}
                  className={`absolute ${colour} text-white rounded-[6px] px-3 flex items-center text-[12.5px] font-semibold tracking-[-0.005em] hover:brightness-110 transition-[filter,box-shadow] focus:outline-none focus:ring-2 focus:ring-gold ${isActive ? "shadow-[0_4px_16px_rgba(0,0,0,0.15)]" : ""}`}
                  style={{
                    top: `${top}px`,
                    left: `${p.leftPct}%`,
                    width: `${p.widthPct}%`,
                    height: `${BAND_HEIGHT}px`,
                    minWidth: "48px",
                  }}
                  aria-label={`${p.name}, ${formatRange(p.typicalStart, p.typicalEnd)}`}
                >
                  <span className="truncate">{p.shortName}</span>
                  {p.startsBeforeWindow && (
                    <span
                      className="absolute left-0 top-0 bottom-0 w-1 bg-white/40 rounded-l-[6px]"
                      aria-hidden
                    />
                  )}
                  {p.endsAfterWindow && (
                    <span
                      className="absolute right-0 top-0 bottom-0 w-1 bg-white/40 rounded-r-[6px]"
                      aria-hidden
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-5 flex flex-wrap gap-5 text-[12px] text-text-muted">
            {Object.entries(CATEGORY_COLOURS).map(([key, c]) => (
              <span key={key} className="inline-flex items-center gap-2">
                <span
                  className={`inline-block w-3 h-3 rounded-sm ${c.band}`}
                  aria-hidden
                />
                {c.label}
              </span>
            ))}
          </div>

          {/* Active-season detail panel (pinned slot, avoids layout shift) */}
          <div className="mt-6 border-t border-border-light pt-5 min-h-[120px]">
            {activeSeason ? (
              <div>
                <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                  <h3 className="font-serif text-[22px] font-bold tracking-[-0.01em]">
                    {activeSeason.name}
                  </h3>
                  <span className="text-[12px] text-text-muted">
                    {formatRange(activeSeason.typicalStart, activeSeason.typicalEnd)} ·{" "}
                    {activeSeason.region}
                  </span>
                </div>
                <p className="text-[14px] text-text-body leading-[1.6] mb-3 max-w-[760px]">
                  {activeSeason.blurb}
                </p>
                <div className="flex flex-wrap gap-2">
                  {activeSeason.workTypes.map((w) => (
                    <span
                      key={w}
                      className="text-[11px] font-semibold uppercase tracking-[1.2px] bg-gold-pale text-navy px-2.5 py-1 rounded-sm"
                    >
                      {w}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-[13px] text-text-muted italic">
                Hover a season to see details, or click to explore.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Mobile vertical timeline */}
      <div className="md:hidden">
        <div className="relative border border-border-light rounded-[16px] bg-white p-4 pb-5">
          {/* Timeline area: month labels left, bars right */}
          <div className="flex">
            {/* Month labels column */}
            <div className="relative w-[36px] shrink-0" style={{ height: "620px" }}>
              {vTicks.map((t, i) => (
                <div
                  key={i}
                  className="absolute left-0 -translate-y-1/2 text-[10px] font-semibold uppercase tracking-[1px] text-text-muted"
                  style={{ top: `${t.topPct}%` }}
                >
                  {t.label}
                </div>
              ))}
            </div>

            {/* Bars area */}
            <div className="relative flex-1" style={{ height: "620px" }}>
              {/* Month gridlines (horizontal) */}
              {vTicks.map((t, i) => (
                <div
                  key={i}
                  className="absolute left-0 right-0 h-px bg-border-light/70"
                  style={{ top: `${t.topPct}%` }}
                  aria-hidden
                />
              ))}

              {/* Today marker */}
              <div
                className="absolute left-0 right-0 h-[2px] bg-gold z-10"
                style={{ top: "0%" }}
                aria-hidden
              >
                <span className="absolute -top-[14px] right-0 text-[9px] font-semibold uppercase tracking-[1.5px] text-gold">
                  Today
                </span>
              </div>

              {/* Season bars */}
              {positioned.map((p, i) => {
                const col = rows[i];
                const colour = bandColourFor(p.category, p.slug);
                const isActive = activeSlug === p.slug;
                const colWidth = 100 / rowCount;
                const gap = 3;
                const leftPct = col * colWidth;
                return (
                  <button
                    key={p.slug}
                    type="button"
                    onClick={() => setActiveSlug(activeSlug === p.slug ? null : p.slug)}
                    className={`absolute ${colour} text-white rounded-[6px] flex items-center justify-center overflow-hidden transition-[box-shadow,filter] focus:outline-none focus:ring-2 focus:ring-gold ${isActive ? "brightness-110 shadow-[0_2px_12px_rgba(0,0,0,0.2)] ring-2 ring-gold z-20" : ""}`}
                    style={{
                      top: `${p.leftPct}%`,
                      height: `${p.widthPct}%`,
                      left: `calc(${leftPct}% + ${gap / 2}px)`,
                      width: `calc(${colWidth}% - ${gap}px)`,
                      minHeight: "28px",
                    }}
                    aria-label={`${p.name}, ${formatRange(p.typicalStart, p.typicalEnd)}`}
                  >
                    <span className="text-[10px] font-semibold leading-[1.2] text-center px-0.5 [writing-mode:vertical-rl] rotate-180">
                      {p.shortName}
                    </span>
                    {p.startsBeforeWindow && (
                      <span
                        className="absolute left-0 right-0 top-0 h-1 bg-white/40 rounded-t-[6px]"
                        aria-hidden
                      />
                    )}
                    {p.endsAfterWindow && (
                      <span
                        className="absolute left-0 right-0 bottom-0 h-1 bg-white/40 rounded-b-[6px]"
                        aria-hidden
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-4 text-[11px] text-text-muted">
            {Object.entries(CATEGORY_COLOURS).map(([key, c]) => (
              <span key={key} className="inline-flex items-center gap-1.5">
                <span
                  className={`inline-block w-2.5 h-2.5 rounded-sm ${c.band}`}
                  aria-hidden
                />
                {c.label}
              </span>
            ))}
          </div>
        </div>

        {/* Mobile detail card (shown on tap) */}
        {activeSeason && (
          <Link
            href={activeSeason.detailUrl}
            className="block mt-3 bg-white border border-gold/40 rounded-[14px] p-4 no-underline text-charcoal shadow-[0_4px_16px_rgba(0,0,0,0.06)]"
          >
            <div className="flex items-baseline gap-2 mb-1">
              <h3 className="font-serif text-[18px] font-bold tracking-[-0.01em] text-navy">
                {activeSeason.shortName}
              </h3>
              <span className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted">
                {CATEGORY_COLOURS[activeSeason.category].label}
              </span>
            </div>
            <div className="text-[12px] text-text-muted mb-2">
              {formatRange(activeSeason.typicalStart, activeSeason.typicalEnd)} · {activeSeason.region}
            </div>
            <p className="text-[13px] text-text-body leading-[1.5] mb-2.5">
              {activeSeason.blurb}
            </p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {activeSeason.workTypes.slice(0, 4).map((w) => (
                <span
                  key={w}
                  className="text-[10px] font-semibold uppercase tracking-[1px] bg-gold-pale text-navy px-2 py-0.5 rounded-sm"
                >
                  {w}
                </span>
              ))}
              {activeSeason.workTypes.length > 4 && (
                <span className="text-[10px] font-semibold text-text-muted">
                  +{activeSeason.workTypes.length - 4}
                </span>
              )}
            </div>
            <span className="text-[12px] font-semibold text-gold">
              View jobs →
            </span>
          </Link>
        )}
      </div>
    </div>
  );
}
